// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * AlertContext
 * 
 * Centralized alert and notification management system
 * Handles game alerts, toast notifications, and alert history
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import eventBus, { EVENT_TYPES } from '../utils/system/EventBus';

const AlertContext = createContext();

// Alert severity levels
export const ALERT_SEVERITY = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  CRITICAL: 'critical',
  ERROR: 'error'
};

// Alert priorities (higher = more important)
export const ALERT_PRIORITY = {
  LOW: 1,
  NORMAL: 2,
  HIGH: 3,
  CRITICAL: 4
};

export const AlertProvider = ({ children }) => {
  // Current alerts (displayed in BottomPanel)
  const [currentAlerts, setCurrentAlerts] = useState([]);
  
  // Full alert history (last 100 alerts)
  const [alertHistory, setAlertHistory] = useState([]);
  
  // Alert queue for managing multiple alerts
  const alertQueueRef = useRef([]);
  const processingRef = useRef(false);

  /**
   * Add a new alert to the system
   * @param {string} message - Alert message
   * @param {string} severity - Alert severity (info, success, warning, critical, error)
   * @param {number} priority - Alert priority (1-4)
   * @param {number} duration - How long to display (ms), 0 = permanent
   * @param {object} metadata - Additional data
   */
  const addAlert = useCallback((message, severity = ALERT_SEVERITY.INFO, priority = ALERT_PRIORITY.NORMAL, duration = 5000, metadata = {}) => {
    const newAlert = {
      id: `alert-${Date.now()}-${Math.random()}`,
      message,
      severity,
      priority,
      duration,
      timestamp: new Date().toLocaleTimeString(),
      timestampFull: new Date().toISOString(),
      metadata,
      dismissed: false
    };

    // Add to queue
    alertQueueRef.current.push(newAlert);
    
    // Add to history immediately
    setAlertHistory(prev => {
      const updated = [newAlert, ...prev];
      return updated.slice(0, 100); // Keep last 100
    });

    // Process queue
    processAlertQueue();

    return newAlert.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Process alert queue with priority sorting
   */
  const processAlertQueue = useCallback(() => {
    if (processingRef.current || alertQueueRef.current.length === 0) {
      return;
    }

    processingRef.current = true;

    // Sort by priority (highest first)
    alertQueueRef.current.sort((a, b) => b.priority - a.priority);

    // Take the highest priority alert
    const nextAlert = alertQueueRef.current.shift();

    // Display it
    setCurrentAlerts([nextAlert]);

    // Auto-dismiss after duration (if not permanent)
    if (nextAlert.duration > 0) {
      setTimeout(() => {
        dismissAlert(nextAlert.id);
        processingRef.current = false;
        processAlertQueue(); // Process next in queue
      }, nextAlert.duration);
    } else {
      processingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Dismiss an alert by ID
   */
  const dismissAlert = useCallback((alertId) => {
    setCurrentAlerts(prev => prev.filter(alert => alert.id !== alertId));
    
    // Mark as dismissed in history
    setAlertHistory(prev => 
      prev.map(alert => 
        alert.id === alertId ? { ...alert, dismissed: true } : alert
      )
    );
  }, []);

  /**
   * Dismiss all current alerts
   */
  const dismissAllAlerts = useCallback(() => {
    setCurrentAlerts([]);
    alertQueueRef.current = [];
    processingRef.current = false;
  }, []);

  /**
   * Clear alert history
   */
  const clearHistory = useCallback(() => {
    setAlertHistory([]);
  }, []);

  /**
   * Get alerts by severity
   */
  const getAlertsBySeverity = useCallback((severity) => {
    return alertHistory.filter(alert => alert.severity === severity);
  }, [alertHistory]);

  /**
   * Get alert statistics
   */
  const getAlertStats = useCallback(() => {
    return {
      total: alertHistory.length,
      info: alertHistory.filter(a => a.severity === ALERT_SEVERITY.INFO).length,
      success: alertHistory.filter(a => a.severity === ALERT_SEVERITY.SUCCESS).length,
      warning: alertHistory.filter(a => a.severity === ALERT_SEVERITY.WARNING).length,
      critical: alertHistory.filter(a => a.severity === ALERT_SEVERITY.CRITICAL).length,
      error: alertHistory.filter(a => a.severity === ALERT_SEVERITY.ERROR).length
    };
  }, [alertHistory]);

  /**
   * Convenience methods for different severity levels
   */
  const info = useCallback((message, metadata = {}) => {
    return addAlert(message, ALERT_SEVERITY.INFO, ALERT_PRIORITY.NORMAL, 5000, metadata);
  }, [addAlert]);

  const success = useCallback((message, metadata = {}) => {
    return addAlert(message, ALERT_SEVERITY.SUCCESS, ALERT_PRIORITY.NORMAL, 5000, metadata);
  }, [addAlert]);

  const warning = useCallback((message, metadata = {}) => {
    return addAlert(message, ALERT_SEVERITY.WARNING, ALERT_PRIORITY.HIGH, 7000, metadata);
  }, [addAlert]);

  const critical = useCallback((message, metadata = {}) => {
    return addAlert(message, ALERT_SEVERITY.CRITICAL, ALERT_PRIORITY.CRITICAL, 10000, metadata);
  }, [addAlert]);

  const error = useCallback((message, metadata = {}) => {
    return addAlert(message, ALERT_SEVERITY.ERROR, ALERT_PRIORITY.CRITICAL, 10000, metadata);
  }, [addAlert]);

  // Listen for gameAlert events (backward compatibility)
  useEffect(() => {
    const unsubscribe = eventBus.on(EVENT_TYPES.GAME_ALERT, (data) => {
      const { message, severity = 'info', priority, duration, metadata } = data;
      addAlert(message, severity, priority, duration, metadata);
    });

    return unsubscribe;
  }, [addAlert]);

  const value = {
    // State
    currentAlerts,
    alertHistory,
    
    // Actions
    addAlert,
    dismissAlert,
    dismissAllAlerts,
    clearHistory,
    
    // Queries
    getAlertsBySeverity,
    getAlertStats,
    
    // Convenience methods
    info,
    success,
    warning,
    critical,
    error,
    
    // Constants
    ALERT_SEVERITY,
    ALERT_PRIORITY
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

/**
 * Hook to use alert system
 */
export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

export default AlertContext;
